import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { makeGetApiCall, makePostApiCall } from "../../shared/makeApiCall";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import ProgressModal from "../../components/modals/ProgressModal";
import {
  buildQaReportOptions,
  getQaReportDefinition,
  QA_REPORT_IDS,
} from "../../shared/qaReportOptions";
import {
  buildFinanceWorkOrderOptionsUrl,
  serializeFinanceReportDates,
} from "../../shared/qaReportDates";
import {
  productionCostYieldReportLabel,
  roomLabel,
  roomsLabel,
  wipLabel,
  wipsLabel,
} from "../../shared/tenantLabels";

const createDefaultFilters = (roomsEntityLabel = "Rooms") => ({
  class: { value: -1, label: "All classes" },
  category: { value: -1, label: "All categories" },
  product: { value: -1, label: "All products" },
  room: { value: -1, label: `All ${roomsEntityLabel.toLowerCase()}` },
  customer: { value: -1, label: "All customers" },
  supplier: { value: -1, label: "All suppliers" },
  issue_type: { value: "", label: "All issue types" },
  lot_number: "",
  work_orders: [],
  weighting_user_id: { value: -1, label: "All weighing users" },
  startDate: null,
  endDate: null,
});

export function QaReportsViewModel(logout) {
  const tenant = useSelector((state) => state.auth.tenant);
  const wipEntityLabel = wipLabel(tenant);
  const wipsEntityLabel = wipsLabel(tenant);
  const roomEntityLabel = roomLabel(tenant);
  const roomsEntityLabel = roomsLabel(tenant);
  const productionCostYieldLabel = productionCostYieldReportLabel(tenant);

  const qaReportOptions = buildQaReportOptions(
    {
      wip: wipEntityLabel,
      wips: wipsEntityLabel,
      room: roomEntityLabel,
      rooms: roomsEntityLabel,
      productionCostYieldReport: productionCostYieldLabel,
    },
    { includeFieldCosts: tenant?.type === "farmer" },
  );
  const [filters, setFilters] = useState(() =>
    createDefaultFilters(roomsEntityLabel),
  );
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [reportType, setReportType] = useState({
    value: -1,
    label: "Select report",
  });
  const [categories, setCategories] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const workOrderRequestId = useRef(0);
  const [suppliers, setSuppliers] = useState([]);
  const [weighingUsers, setWeighingUsers] = useState([]);

  useEffect(() => {
    workOrderRequestId.current += 1;
    setWorkOrders([]);
  }, [filters.startDate, filters.endDate]);

  const showProgressModal = (message) => {
    setEmptyModelOpen({
      open: true,
      childComponent: <ProgressModal message={message} />,
    });
  };

  const showErrorModal = useCallback((title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  }, []);

  const getCategoryTypes = useCallback(async function () {
    if (filters.class.value === -1) {
      return true;
    }
    setCategoryLoading(true);
    let callResult = await makeGetApiCall(
      `categoriesForDropdownWithClass/${filters.class.value}`
    );
    if (callResult.success) {
      setCategories([
        { value: -1, label: "All categories" },
        ...callResult.data,
      ]);
      setCategoryLoading(false);
      return true;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch category types");
      setCategoryLoading(false);
      return false;
    }
  }, [filters.class.value, logout, showErrorModal]);

  useEffect(() => {
    if (filters.class.value === -1) {
      setCategories([{ value: -1, label: "All categories" }]);
      setCategoryLoading(false);
    } else {
      getCategoryTypes();
    }
  }, [filters.class.value, getCategoryTypes]);

  const getClassTypes = useCallback(async function () {
    let callResult = await makeGetApiCall("classesForDropdown");
    if (callResult.success) {
      setClasses([{ value: -1, label: "All classes" }, ...callResult.data]);
      return true;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      return false;
    }
  }, [logout]);

  const getCustomersForDropdown = useCallback(async function () {
    let callResult = await makeGetApiCall(`customersForDropdown`);
    if (callResult.success) {
      setCustomers([{ value: -1, label: "All customers" }, ...callResult.data]);
      return true;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      return false;
    }
  }, [logout]);

  const getProductOptionsForDropdown = useCallback(async function () {
    let callResult = await makeGetApiCall(`productsForDropdown`);

    if (callResult.success) {
      setProducts([{ value: -1, label: "All products" }, ...callResult.data]);
      return true;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      return false;
    }
  }, [logout]);

  const getRoomsForDropdown = useCallback(async function () {
    let callResult = await makeGetApiCall("roomsForDropdown");

    if (callResult.success) {
      setRooms([
        { value: -1, label: `All ${roomsEntityLabel.toLowerCase()}` },
        ...callResult.data,
      ]);
      return true;
    }
    if (callResult.errorStatus === 401) {
      logout();
    }
    showErrorModal(`Could not fetch ${roomsEntityLabel.toLowerCase()}`);
    return false;
  }, [logout, roomsEntityLabel, showErrorModal]);

  const getWorkOrdersForDropdown = useCallback(async function () {
    const requestId = ++workOrderRequestId.current;
    const url = buildFinanceWorkOrderOptionsUrl(
      filters.startDate,
      filters.endDate,
    );
    if (!url) {
      setWorkOrders([]);
      return false;
    }

    const callResult = await makeGetApiCall(url);

    if (requestId !== workOrderRequestId.current) {
      return false;
    }

    if (callResult.success) {
      setWorkOrders(callResult.data);
      return true;
    }
    if (callResult.errorStatus === 401) {
      logout();
    }
    showErrorModal(`Could not fetch ${wipsEntityLabel.toLowerCase()}`);
    return false;
  }, [
    filters.endDate,
    filters.startDate,
    logout,
    showErrorModal,
    wipsEntityLabel,
  ]);

  const getSupplierOptionsForDropdown = useCallback(async function () {
    let callResult = await makeGetApiCall(`suppliersForDropdown`);

    if (callResult.success) {
      setSuppliers([{ value: -1, label: "All suppliers" }, ...callResult.data]);
      return true;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch suppliers");
      return false;
    }
  }, [logout, showErrorModal]);

  const getWeighingUsersForDropdown = useCallback(async function () {
    let callResult = await makeGetApiCall("icapWeightingUsersForDropdown");
    if (callResult.success) {
      setWeighingUsers([
        { value: -1, label: "All weighing users" },
        ...callResult.data,
      ]);
      return true;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showErrorModal("Could not fetch weighing users");
      }
      return false;
    }
  }, [logout, showErrorModal]);

  const printSelectedReport = async function () {
    const reportDefinition = getQaReportDefinition(reportType.value);

    if (
      reportDefinition?.requiresDateRange &&
      (!filters.startDate || !filters.endDate)
    ) {
      const reportName = reportType.label;
      showErrorModal(
        "Please select a date range",
        `A start date and end date are required for the ${reportName} Report.`,
      );
      return;
    }

    if (
      reportType.value === QA_REPORT_IDS.inventoryFlow &&
      (filters.product?.value === -1 || !filters.product?.plu)
    ) {
      showErrorModal(
        "Please select a product",
        "The Flow Report follows inventory movement for one product at a time.",
      );
      return;
    }

    if (
      reportType.value === QA_REPORT_IDS.employeeWeighing &&
      filters.weighting_user_id?.value === -1
    ) {
      showErrorModal("Please select a weighing user", "A weighing user is required for the Employee Weighing Report.");
      return;
    }
    showProgressModal("Generating report...");
    const reportUrl = reportDefinition?.endpoint ?? "error_type";

    const reportFilters = {
      ...filters,
      endDate: reportDefinition?.useStartDateAsEndDate
        ? filters.startDate
        : filters.endDate,
    };
    const submittedFilters = reportDefinition?.serializeDates
      ? serializeFinanceReportDates(reportFilters)
      : reportFilters;

    let callResult = await makePostApiCall(reportUrl, {
      filter: submittedFilters,
    });

    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
      setEmptyModelOpen({ open: false, childComponent: null });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      setEmptyModelOpen({ open: false, childComponent: null });
      showErrorModal(callResult.data.message);
    }

    return callResult;
  };

  const resetFilters = useCallback(() => {
    setFilters(createDefaultFilters(roomsEntityLabel));
  }, [roomsEntityLabel]);

  const resetReportSelection = useCallback(() => {
    setReportType({
      value: -1,
      label: "Select report",
    });
    resetFilters();
  }, [resetFilters]);

  return {
    emptyModal,
    setEmptyModelOpen,
    filters,
    printSelectedReport,
    qaReportOptions,
    setFilters,
    reportType,
    setReportType,
    resetFilters,
    resetReportSelection,
    categories,
    categoryLoading,
    customers,
    products,
    rooms,
    workOrders,
    roomEntityLabel,
    wipsEntityLabel,
    suppliers,
    getCategoryTypes,
    getCustomersForDropdown,
    getProductOptionsForDropdown,
    getRoomsForDropdown,
    getWorkOrdersForDropdown,
    getSupplierOptionsForDropdown,
    classes,
    getClassTypes,
    weighingUsers,
    getWeighingUsersForDropdown,
  };
}
